import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch } from '../api/client'
import type { DataKeysResponse, DataSnapshotsResponse } from '../api/types'

// useDataKeysAtTime fetches the directory-style data listing. With no
// `at`, returns the live state; with an RFC 3339 timestamp, returns
// the state of every key that was live at that moment.
//
// Query key includes the literal `at` value (or "live"), so
// useDataRestore can invalidate the whole family by prefix
// (`['data-keys', glowID]`) without naming each snapshot key.
export function useDataKeysAtTime(glowID: string, at: string | null) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['data-keys', glowID, at ?? 'live'],
    queryFn: ({ signal }) => {
      const q = new URLSearchParams()
      if (at) {
        q.set('at', at)
      }
      const suffix = q.toString() ? `?${q.toString()}` : ''
      return apiFetch<DataKeysResponse>(
        `/v1/glows/${glowID}/data-keys${suffix}`,
        idTokenProvider,
        { signal },
      )
    },
    enabled: !!user && !!glowID,
  })
}

// useDataSnapshots fetches the per-minute bucket list for the
// VersionPicker. v1 ships unpaginated — the server caps at 100 rows
// per page and the picker only shows the newest 50 anyway. A future
// "show older" affordance can wire the `next_cursor` through.
export function useDataSnapshots(glowID: string) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['data-snapshots', glowID],
    queryFn: ({ signal }) =>
      apiFetch<DataSnapshotsResponse>(
        `/v1/glows/${glowID}/data-snapshots`,
        idTokenProvider,
        { signal },
      ),
    enabled: !!user && !!glowID,
    staleTime: 60_000,
  })
}
